import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "../../components/atoms/card";
import { todayCalendarDate } from "../../helper/dateTime";
import { useGetOperationsCalendarQuery } from "../../store/apiSlice/operationsApiSlice";
import {
  calendarEventKindLabel,
  calendarMarkClass,
  calendarPreviewClass,
  eventsForDay,
  fourWeekRange,
  groupEventsByDate,
  monthDayRange,
  parseCalendarDate,
  rollingDayRange,
  upcomingCalendarEvents,
} from "./operationsCalendarHelpers";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Monday-based column offset for the first of a month.
 * @param {string} isoDate
 * @return {number}
 */
function mondayOffset(isoDate) {
  return (parseCalendarDate(isoDate).getUTCDay() + 6) % 7;
}

/**
 * @param {string} isoDate
 * @return {string}
 */
function weekdayName(isoDate) {
  return new Intl.DateTimeFormat("en-AU", { weekday: "short", timeZone: "UTC" }).format(parseCalendarDate(isoDate));
}

/**
 * @param {string} isoDate
 * @return {string}
 */
function formatDayHeading(isoDate) {
  return new Intl.DateTimeFormat("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(parseCalendarDate(isoDate));
}

/**
 * @param {string} isoDate
 * @return {string}
 */
function formatShortDate(isoDate) {
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(parseCalendarDate(isoDate));
}

/** @param {Object} props Day cell properties. @return {JSX.Element} */
function DayCell({ date, isoToday, selected, events, onSelect }) {
  const preview = events.slice(0, 2);
  const extra = events.length - preview.length;
  const isToday = date === isoToday;
  const isPast = date < isoToday;
  const dayNum = parseCalendarDate(date).getUTCDate();
  const dayName = weekdayName(date);
  return (
    <button
      type="button"
      className={`operations-calendar-day${selected ? " operations-calendar-day--selected" : ""}${
        isToday ? " operations-calendar-day--today" : ""
      }${isPast ? " operations-calendar-day--past" : ""}${events.length ? " operations-calendar-day--busy" : ""}`}
      onClick={() => onSelect(date)}
      aria-pressed={selected}
      aria-label={`${dayName} ${date}, ${events.length} item${events.length === 1 ? "" : "s"}`}
    >
      <span className="operations-calendar-day-num">{dayNum}</span>
      {preview.length > 0 && (
        <span className="operations-calendar-previews">
          {preview.map(event => (
            <span key={event.id} className={`operations-calendar-preview ${calendarPreviewClass(event)}`}>
              {event.label}
            </span>
          ))}
          {extra > 0 && <span className="operations-calendar-more">+{extra}</span>}
        </span>
      )}
    </button>
  );
}

/** @param {Object} props One calendar event row. @return {JSX.Element} */
function EventRow({ event, showDate }) {
  return (
    <li>
      <span className={`operations-calendar-detail-kind ${calendarMarkClass(event)}`}>
        {calendarEventKindLabel(event)}
      </span>
      {event.detail_url ? (
        <Link to={event.detail_url}>{event.label}</Link>
      ) : (
        <span>{event.label}</span>
      )}
      {event.overdue && <span className="operations-calendar-detail-overdue">Overdue</span>}
      {(showDate || event.route) && (
        <span className="operations-calendar-detail-meta">
          {showDate ? formatShortDate(event.date) : ""}
          {showDate && event.route ? " · " : ""}
          {event.route || ""}
        </span>
      )}
    </li>
  );
}

/**
 * @param {Object[]} events
 * @return {{ date: string, events: Object[] }[]}
 */
function groupByDate(events) {
  const groups = [];
  events.forEach(event => {
    const current = groups[groups.length - 1];
    if (current && current.date === event.date) current.events.push(event);
    else groups.push({ date: event.date, events: [event] });
  });
  return groups;
}

/**
 * @param {Object[]} events
 * @return {string}
 */
function upcomingSummary(events) {
  const tasks = events.filter(event => event.kind === "task").length;
  const shipments = events.length - tasks;
  const parts = [];
  if (shipments) parts.push(`${shipments} shipment${shipments === 1 ? "" : "s"}`);
  if (tasks) parts.push(`${tasks} task${tasks === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

/**
 * Side panel beside the calendar. A selected day with items replaces the upcoming list.
 * @param {Object} props Panel properties.
 * @return {JSX.Element}
 */
function CalendarSidePanel({ selectedDate, selectedEvents, upcoming, timezone, onSelectDate, onShowUpcoming }) {
  const showingDay = selectedEvents.length > 0;
  const groups = groupByDate(upcoming);
  return (
    <div className="operations-calendar-detail">
      <div className="operations-calendar-detail-head">
        <h3>{showingDay ? formatDayHeading(selectedDate) : "Upcoming"}</h3>
        {showingDay && (
          <button type="button" className="operations-calendar-detail-back" onClick={onShowUpcoming}>
            All upcoming
          </button>
        )}
      </div>
      {!showingDay && upcoming.length > 0 && (
        <p className="operations-calendar-detail-count">{upcomingSummary(upcoming)}</p>
      )}
      <ul className="operations-calendar-legend">
        <li><span className="operations-calendar-mark operations-calendar-mark--eta" />Shipment</li>
        <li><span className="operations-calendar-mark operations-calendar-mark--task" />Task</li>
        <li><span className="operations-calendar-mark operations-calendar-mark--late" />Overdue</li>
      </ul>
      {showingDay ? (
        <ul className="operations-calendar-detail-list">
          {selectedEvents.map(event => (
            <EventRow key={event.id} event={event} showDate={false} />
          ))}
        </ul>
      ) : groups.length > 0 ? (
        groups.map(group => (
          <div key={group.date} className="operations-calendar-detail-group">
            <button type="button" className="operations-calendar-detail-date" onClick={() => onSelectDate(group.date)}>
              {formatShortDate(group.date)}
            </button>
            <ul className="operations-calendar-detail-list">
              {group.events.map(event => (
                <EventRow key={event.id} event={event} showDate={false} />
              ))}
            </ul>
          </div>
        ))
      ) : (
        <p className="operations-calendar-detail-note">No upcoming tasks or shipments.</p>
      )}
      {timezone && <p className="operations-calendar-detail-tz">{timezone}</p>}
    </div>
  );
}

/**
 * @param {Object} props Calendar card properties.
 * @return {JSX.Element|null}
 */
export function OperationsComingUpCalendar({ data, localDate, enabled = true }) {
  const isoToday = localDate || todayCalendarDate(data?.timezone);
  const [view, setView] = useState("agenda");
  const [selectedDate, setSelectedDate] = useState("");
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const parsed = parseCalendarDate(isoToday);
    return { year: parsed.getUTCFullYear(), month: parsed.getUTCMonth() };
  });
  const heldCalendar = useRef(null);

  const weekDays = useMemo(() => {
    const start = parseCalendarDate(fourWeekRange(isoToday)[0]);
    start.setUTCDate(start.getUTCDate() + weekOffset * 7);
    return rollingDayRange(start, 28);
  }, [isoToday, weekOffset]);
  const monthDays = useMemo(
    () => monthDayRange(monthAnchor.year, monthAnchor.month),
    [monthAnchor.month, monthAnchor.year],
  );
  const visibleDays = view === "month" ? monthDays : weekDays;
  const range = useMemo(
    () => ({ from: visibleDays[0], to: visibleDays[visibleDays.length - 1] }),
    [visibleDays],
  );

  const canFetch =
    enabled &&
    Boolean(data?.permissions?.can_view_shipments || data?.permissions?.can_view_notes);
  const { data: calendarData, isFetching } = useGetOperationsCalendarQuery(
    { ...range, localDate: isoToday },
    { skip: !canFetch },
  );
  if (calendarData) heldCalendar.current = calendarData;
  const shownCalendar = calendarData || heldCalendar.current;

  const events = useMemo(() => {
    const rows = shownCalendar?.events || [];
    const from = visibleDays[0];
    const to = visibleDays[visibleDays.length - 1];
    return rows.filter(event => event.date >= from && event.date <= to);
  }, [shownCalendar, visibleDays]);
  const byDate = useMemo(() => groupEventsByDate(events), [events]);
  const upcoming = useMemo(() => upcomingCalendarEvents(events, isoToday), [events, isoToday]);

  const canShow =
    shownCalendar?.permissions?.can_view_shipments || shownCalendar?.permissions?.can_view_notes;
  if (!canShow) return null;

  const selectedEvents = eventsForDay(events, selectedDate);
  const todayParts = parseCalendarDate(isoToday);
  const viewingCurrentMonth =
    monthAnchor.year === todayParts.getUTCFullYear() &&
    monthAnchor.month === todayParts.getUTCMonth();
  const showBackToToday = view === "month" ? !viewingCurrentMonth : weekOffset !== 0;
  const monthLabel = new Intl.DateTimeFormat("en-AU", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(monthAnchor.year, monthAnchor.month, 1)));
  const windowLabel = weekOffset === 0
    ? "Next 4 weeks"
    : `${formatShortDate(weekDays[0])} – ${formatShortDate(weekDays[weekDays.length - 1])}`;

  /** @param {number} delta Month navigation delta. */
  const shiftMonth = delta => {
    setMonthAnchor(current => {
      const next = new Date(Date.UTC(current.year, current.month + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
    });
  };

  const backToToday = () => {
    setWeekOffset(0);
    setMonthAnchor({ year: todayParts.getUTCFullYear(), month: todayParts.getUTCMonth() });
    setSelectedDate("");
  };

  return (
    <Card as="section" className="operations-panel operations-calendar" aria-label="Calendar">
      <div className="operations-calendar-header">
        <div className="operations-calendar-header-slot">
          {showBackToToday && (
            <button type="button" className="operations-calendar-today" onClick={backToToday}>
              Back to today
            </button>
          )}
        </div>
        <div className="operations-calendar-view-toggle" role="group" aria-label="Calendar view">
          <button type="button" className={view === "agenda" ? "is-active" : ""} onClick={() => setView("agenda")}>
            4 weeks
          </button>
          <button type="button" className={view === "month" ? "is-active" : ""} onClick={() => setView("month")}>
            Month
          </button>
        </div>
        <div className="operations-calendar-header-slot operations-calendar-header-slot--end">
          {view === "month" ? (
            <div className="operations-calendar-pager">
              <button type="button" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
                ←
              </button>
              <span>{monthLabel}</span>
              <button type="button" aria-label="Next month" onClick={() => shiftMonth(1)}>
                →
              </button>
            </div>
          ) : (
            <div className="operations-calendar-pager">
              <button type="button" aria-label="Previous weeks" onClick={() => setWeekOffset(current => current - 1)}>
                ←
              </button>
              <span>{windowLabel}</span>
              <button type="button" aria-label="Next weeks" onClick={() => setWeekOffset(current => current + 1)}>
                →
              </button>
            </div>
          )}
          {isFetching && <span className="operations-calendar-refresh">Updating…</span>}
        </div>
      </div>

      <div className="operations-calendar-body">
        <div
          className={`operations-calendar-grid operations-calendar-grid--${view}`}
          role="grid"
          aria-label={view === "agenda" ? "Next 4 weeks" : monthLabel}
        >
          {WEEKDAYS.map(name => (
            <span key={name} className="operations-calendar-weekday">{name}</span>
          ))}
          {view === "month" && Array.from({ length: mondayOffset(visibleDays[0]) }, (_, index) => (
            <span key={`pad-${index}`} />
          ))}
          {visibleDays.map(day => (
            <DayCell
              key={day}
              date={day}
              isoToday={isoToday}
              selected={selectedDate === day}
              events={byDate.get(day) || []}
              onSelect={setSelectedDate}
            />
          ))}
        </div>
        <CalendarSidePanel
          selectedDate={selectedDate}
          selectedEvents={selectedEvents}
          upcoming={upcoming}
          timezone={shownCalendar?.timezone || data?.timezone}
          onSelectDate={setSelectedDate}
          onShowUpcoming={() => setSelectedDate("")}
        />
      </div>
    </Card>
  );
}
